import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';
import { RecentTags, rememberTag } from '../components/RecentTags';

const RARITY_LABEL = { common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary', champion: 'Champion' };

function fmtGold(g) {
  if (g == null) return '—';
  return g >= 1000 ? `${(g / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k` : g.toLocaleString();
}

/* Clash Royale upgrade advisor: which card buys the most win rate per 1,000
   gold. Gold balance isn't in the API, so the player types a budget and the
   list filters to what they can afford rather than us guessing. */
function ClashAdvisor() {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(tag || '');
  const [scope, setScope] = useState('deck');
  const [budget, setBudget] = useState('');
  const [readyOnly, setReadyOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  usePageMeta({
    title: data?.player?.name ? `${data.player.name} · Upgrades` : 'Clash Royale Upgrade Advisor',
    description: 'Enter your Clash Royale player tag to see which card upgrades add the most win rate for the gold and cards you have.',
    noindex: !!tag,
  });

  useEffect(() => { setInput(tag || ''); }, [tag]);

  useEffect(() => {
    if (!tag) { setData(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getClashAdvice(tag, scope)
      .then((d) => { if (!cancelled) { setData(d); rememberTag('clash', tag, d?.player?.name); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tag, scope, nonce]);

  const rows = useMemo(() => {
    const all = data?.upgrades || [];
    const cap = parseInt(String(budget).replace(/[^0-9]/g, ''), 10);
    return all.filter((r) =>
      (!readyOnly || r.ready) && (Number.isNaN(cap) || r.gold == null || r.gold <= cap));
  }, [data, budget, readyOnly]);

  function submit(e) {
    e.preventDefault();
    const t = input.trim().replace(/^#/, '').toUpperCase();
    if (t) navigate(`/clash/${encodeURIComponent(t)}`);
  }

  const deck = data?.deck;
  const model = data?.model;

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">Clash Royale</div>
            <h1>Upgrade Advisor</h1>
            <div className="sub">
              Which card should you upgrade next? Every row is priced in gold and valued in win rate,
              measured from {model ? `${model.n_battles.toLocaleString()} ` : ''}real Trophy Road battles.
            </div>
          </div>
        </div>

        <form className="filters" onSubmit={submit}>
          <input
            className="search-input"
            placeholder="Your player tag, e.g. #2PP"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Clash Royale player tag"
          />
          <button type="submit" className="retry-btn">Rank my upgrades</button>
        </form>
        <RecentTags game="clash" current={tag} onPick={(t) => navigate(`/clash/${encodeURIComponent(t)}`)} />

        {tag && (
          <div className="filters">
            <div className="seg">
              <button type="button" className={scope === 'deck' ? 'on' : ''} onClick={() => setScope('deck')}>Current deck</button>
              <button type="button" className={scope === 'all' ? 'on' : ''} onClick={() => setScope('all')}>Whole collection</button>
            </div>
            <input
              className="search-input"
              inputMode="numeric"
              placeholder="Gold you have (optional)"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              aria-label="Gold budget"
            />
            <div className="seg">
              <button type="button" className={!readyOnly ? 'on' : ''} onClick={() => setReadyOnly(false)}>All</button>
              <button type="button" className={readyOnly ? 'on' : ''} onClick={() => setReadyOnly(true)}>Enough copies</button>
            </div>
          </div>
        )}

        {!tag ? (
          <div className="tip">
            Enter your tag (Profile → under your name in game). We read your cards from the official API,
            then rank each possible upgrade by <b>win-rate points gained per 1,000 gold</b>.
          </div>
        ) : error ? (
          <ErrorMessage message={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : loading || !data ? (
          <SkeletonRows rows={8} />
        ) : (
          <>
            <div className="phead" style={{ marginTop: 6 }}>
              <div>
                <h2 style={{ margin: 0 }}>{data.player.name}</h2>
                <div className="sub">{data.player.tag} · {data.player.trophies?.toLocaleString() ?? '—'} trophies</div>
              </div>
            </div>

            {deck && model && (
              <div className="stat-ribbon">
                <div className="srib"><div className="v">{deck.mean_level.toFixed(2)}</div><div className="l">Deck level (cap {deck.cap})</div></div>
                <div className="srib"><div className="v">{deck.min_level}</div><div className="l">Weakest card</div></div>
                <div className="srib"><div className="v">{deck.maxed}/8</div><div className="l">Cards maxed</div></div>
                <div className="srib"><div className="v">+{model.card_upgrade_pts.toFixed(2)}%</div><div className="l">Win rate per card level</div></div>
              </div>
            )}

            <div className="tablecard">
              <div className="table-scroll">
                <table className="meta">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Card</th>
                      <th>Level</th>
                      <th>Copies</th>
                      <th>Gold</th>
                      <th>Win rate</th>
                      <th>Per 1k gold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.id}>
                        <td className="rank">{i + 1}</td>
                        <td>
                          <div className="pcell">
                            <div>
                              <div className="pn">{r.name}</div>
                              <div className="pt">{RARITY_LABEL[r.rarity] || r.rarity}{r.elixir != null ? ` · ${r.elixir} elixir` : ''}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}><span className="num-cell">{r.level} → {r.level + 1}</span></td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className="num-cell">
                            {r.count.toLocaleString()}{r.copies_needed != null ? ` / ${r.copies_needed.toLocaleString()}` : ''}
                          </span>
                          {r.ready && <> <span className="pill-chip lime">ready</span></>}
                        </td>
                        <td><span className="num-cell">{fmtGold(r.gold)}</span></td>
                        <td><span className="trend up">+{r.gain_pts.toFixed(2)}%</span></td>
                        <td><span className="num-cell">{r.pts_per_1k_gold != null ? `+${r.pts_per_1k_gold.toFixed(3)}%` : '—'}</span></td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={7} className="empty-state">
                        {(data.upgrades || []).length === 0 ? 'Nothing to upgrade here: every card in scope is maxed.' : 'No upgrade fits that filter.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="tip">
              <b>How this is ranked.</b> One level on one card is worth about +{model.card_upgrade_pts.toFixed(2)} win-rate
              points (one eighth of the {model.global_pts.toFixed(1)}-point effect of a whole-deck level, ±{(model.global_se_pts * 1.96).toFixed(1)}).
              We can't yet measure reliably which cards matter more than others, so every card carries that same gain
              and the list puts the <b>cheapest upgrade first</b>: same win rate, less gold.
              {!data.costs_verified && ' Gold costs are unavailable for this card level cap, so the list is ordered by win-rate gain then lowest level.'}
            </div>
            <div className="tip">
              Looking for something to play with your maxed cards? <Link to={`/clash/decks/${encodeURIComponent(tag)}`}>See deck ideas for your cards →</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ClashAdvisor;
